/**
 * The draft of one inspector input that commits on Enter or blur, kept
 * across changes of the value underneath it — so committing, or someone
 * else's edit landing, never remounts the input (which dropped focus and the
 * caret: the "inspector focus loss on commit" bug, where each control was
 * keyed by its value and a commit's new value made React replace it).
 *
 *   the value changes, the draft is clean (it still shows the old value,
 *   or what this input just committed)   → the draft follows the new value
 *   the value changes while the user has typed something else → the draft
 *   stays (their edit is not thrown away mid-word; committing it is a
 *   normal later write, LWW)
 *
 * Adjusting state while rendering is React's documented pattern for state
 * derived from a changing prop ("storing information from previous renders");
 * no effect, so there is no frame showing the stale draft.
 */
import { useState } from 'react';

export type FieldDraft = {
  readonly draft: string;
  readonly setDraft: (next: string) => void;
  /**
   * Whether `draft` differs from what the field holds and from what this
   * input last sent (so Enter's blur does not send it again).
   */
  readonly dirty: boolean;
  /** Record that `value` was just committed from this input: when the field takes it, the draft follows along. */
  readonly markCommitted: (value: string) => void;
  /**
   * Focus came back: forget what was sent. A commit the session refused (a
   * full offline queue) left the value unchanged, and the draft it left must
   * be committable again (review).
   */
  readonly rearm: () => void;
  /** Back to the field's value (Escape). */
  readonly reset: () => void;
};

export function useFieldDraft(value: string): FieldDraft {
  const [draft, setDraft] = useState(value);
  const [seen, setSeen] = useState(value);
  const [sent, setSent] = useState<string | null>(null);
  if (value !== seen) {
    setSeen(value);
    if (draft === seen || draft === sent) setDraft(value);
    setSent(null);
  }
  return {
    draft,
    setDraft,
    dirty: draft !== value && draft !== sent,
    markCommitted: (committed) => setSent(committed),
    rearm: () => setSent(null),
    reset: () => setDraft(value),
  };
}
